import type { ChipTone } from '@/components/editorial/StatusChip';
import type { TKey } from '@/i18n/msg';

/**
 * Community-page display tokens (2026-09-10 rebuild).
 *
 * Pure data — no React, no DOM. The category tint table that used to live
 * here is gone with the categories: a card says who, when and what.
 *
 * Every label is a `community:` catalog KEY (#2109 R3-B4), resolved at
 * render with `t()` / `labelFor`, so a language switch re-renders it. No
 * staff file imports these maps, so there is no English getter.
 */

/**
 * Everything the feed can be narrowed by: a date scope. "Only my notes" left
 * with the family composer (#2174: Community is TRF announcements only).
 */
export type CommunityWhen = '' | 'week' | 'month' | 'older';

export interface CommunityFilters {
  when: CommunityWhen;
}

export const DEFAULT_COMMUNITY_FILTERS: CommunityFilters = { when: '' };

export const ALL_COMMUNITY_WHEN: readonly CommunityWhen[] = ['', 'week', 'month', 'older'];

export const WHEN_OPTIONS: readonly { value: CommunityWhen; labelKey: TKey }[] = [
  { value: '', labelKey: 'community:filter.when.any' },
  { value: 'week', labelKey: 'community:filter.when.week' },
  { value: 'month', labelKey: 'community:filter.when.month' },
  { value: 'older', labelKey: 'community:filter.when.older' },
];

export function isCommunityFiltered(f: CommunityFilters): boolean {
  return f.when !== '';
}

/**
 * The three reactions, in display order. The visible mark is a symbol and
 * nothing else (Victor 2026-09-10: "heart, not symbol Heart"); the word is the
 * accessible name. The symbol is a drawn icon, not an emoji (#1951, Victor
 * 2026-09-12: "remove any emojis") — the icon for each key lives beside the
 * button in `CommunityPostCard`, because this module stays pure data. Keys
 * match `REACTION_TYPES` in `backend/src/models/CommunityPost.js`.
 */
export type ReactionKey = 'heart' | 'hug' | 'celebrate';

export const REACTIONS: readonly { key: ReactionKey; labelKey: TKey }[] = [
  { key: 'heart', labelKey: 'community:reaction.heart' },
  { key: 'hug', labelKey: 'community:reaction.hug' },
  { key: 'celebrate', labelKey: 'community:reaction.celebrate' },
];

export type CommunityPostStatus = 'pending' | 'visible' | 'removed';
export const ALL_COMMUNITY_POST_STATUSES: readonly CommunityPostStatus[] = ['pending', 'visible', 'removed'];

/** What a note says to its own author. Backend statuses: pending | visible | removed. */
export const STATUS_TOKENS: Record<CommunityPostStatus, { labelKey: TKey; tone: ChipTone }> = {
  pending: { labelKey: 'community:status.pending', tone: 'warn' },
  visible: { labelKey: 'community:status.visible', tone: 'ok' },
  // `removed` keeps danger: unlike a category, this IS a negative outcome.
  removed: { labelKey: 'community:status.removed', tone: 'danger' },
};

/** The one line the author line uses for a staff announcement. */
export const ANNOUNCEMENT_AUTHOR_KEY: TKey = 'community:author.announcement';

/**
 * The author line of an anonymous note. The server stores the English
 * "A TRF family" in `author_display` (`backend/src/routes/community.js`);
 * the card ignores that and renders this key whenever `post.anonymous` is
 * true, so the line reads in the reader's language.
 */
export const ANONYMOUS_AUTHOR_KEY: TKey = 'community:author.anonymous';

// ── Announcements: pin and expiry (w1023c) ─────────────────────────────────

/**
 * The `(?)` ids of the w1023c announcement defaults. `AnnouncementDecision`
 * renders each one once its entry exists in `lib/pending-decisions.ts`.
 */
export const ANNOUNCEMENT_DECISION = {
  audienceShape: 'announcement-audience-shape',
  onePin: 'one-pin',
  audienceFilter: 'announcement-audience-filter',
} as const;

export type AnnouncementDecisionId = (typeof ANNOUNCEMENT_DECISION)[keyof typeof ANNOUNCEMENT_DECISION];

/** The mark on the one pinned announcement, in the reader's language. */
export const PINNED_KEY: TKey = 'community:card.pinned';

/**
 * The pinned item first, everything else in the order given. The feed merges
 * announcements and polls by date on the client, so the server's
 * pinned-first sort has to be re-applied after the merge.
 */
export function pinnedFirst<T>(items: readonly T[], isPinned: (item: T) => boolean): T[] {
  const pinned = items.filter(isPinned);
  return pinned.length ? [...pinned, ...items.filter((i) => !isPinned(i))] : [...items];
}

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A calendar day (YYYY-MM-DD) as words, e.g. "Oct 31, 2026". The day is a
 * day, not an instant: it is formatted at UTC midnight IN UTC, so it reads
 * the same in every zone. `new Date('2026-10-31')` read in local time is
 * Oct 30 everywhere west of UTC, the B-432 class of bug.
 */
export function formatCalendarDay(ymd: string | null | undefined, locale = 'en-US'): string | null {
  const m = ymd ? CALENDAR_DATE.exec(ymd) : null;
  if (!m) return null;
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(at.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' }).format(at);
}

/** Today as YYYY-MM-DD on the wall clock of `timeZone` (default: this device). */
export function todayCalendarDate(now: Date = new Date(), timeZone?: string): string {
  // en-CA formats as YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}
